import {ChangeDetectorRef, Component, OnDestroy, OnInit, Optional} from '@angular/core';
import {DATE_FORMAT_YMD_HM, Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {Subscription} from 'rxjs';

import {SettingsClientCustomService} from '../../services/settings-client-custom.service';
import {SettingsClientCustomCrudService} from '../../services/settings-client-custom-crud.service';
import {TRANSLATION_GRID_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ClientCustomFieldDefinition} from '../../../../types';


// hack to inject decorator declarations. must occur before class declaration!
export interface SettingsClientCustomGridComponent extends Translatable {}

@Component({
  selector: 'app-settings-client-custom-grid',
  templateUrl: './settings-client-custom-grid.component.html',
  styleUrls: ['./settings-client-custom-grid.component.css']
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class SettingsClientCustomGridComponent implements OnInit, OnDestroy {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public definitions: ClientCustomFieldDefinition[] = [];
  public filter: ClientCustomFieldDefinition.Filter = {};
  public total = 0;

  /**
   * Timeout for filter change event.
   */
  private filterChangeTimeout: any = null;

  /**
   * Timeout for show preloader event.
   */
  private showPreloaderTimeout: any = null;

  /**
   * Subscriptions.
   */
  private subscriptions: Subscription[] = [];

  /**
   * Date format to use in grid.
   */
  public get dateFormat() {
    const format = DATE_FORMAT_YMD_HM.replace(/YYYY/, 'YY');
    return this.getDateFormat(format);
  }

  /**
   * Refreshes the custom fields grid data
   */
  public async onRefresh() {
    this.showPreloader();
    await new Promise(resolve => setTimeout(resolve, 500));
    this.loadDefinitions();
  }

  /**
   * Loads definitions and initializes search filter.
   */
  public ngOnInit() {
    this.layoutService.showPreloader();
    this.loadDefinitions();
  }

  /**
   * Unsubscribes and deletes subscriptions on destroy event.
   */
  public ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions = [];
  }

  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    private customFieldService: SettingsClientCustomService,
    private crudService: SettingsClientCustomCrudService,
    private ref: ChangeDetectorRef
  ) {
    this.subscriptions.push(
      this.customFieldService.onDefinitionSaved().subscribe(() => this.loadDefinitions())
    );
  }

  /**
   * Opens the create modal.
   */
  public async onCreateDefinition() {
    this.crudService.openModal();
  }

  /**
   * Opens the edit modal.
   *
   * @param definition
   */
  public async onEditDefinition(definition: ClientCustomFieldDefinition) {
    this.crudService.openModal(definition.uid);
  }

  /**
   * Deletes a definition after confirmation.
   *
   * @param definition
   */
  public async onDeleteDefinition(definition: ClientCustomFieldDefinition) {
    const message = `${this._(KEYS.GRID.DELETE_DEFINITION_WARNING)}<br><br>`
      + `${this._(KEYS.GRID.DELETE_DEFINITION_QUESTION)} ${definition.label} (${definition.key})`;

    const sure = await confirm(message);

    if (sure) {
      try {
        await this.customFieldService.deleteCustomField(definition);
        this.definitions.splice(this.definitions.indexOf(definition), 1);
        alert(this._(KEYS.GRID.SUCCESSFULLY_DELETED_DEFINITION));
      } catch (error) {
        console.error(error);
        alert(this._(KEYS.GRID.ERROR_DELETING_DEFINITION));
      }
    }
  }

  /**
   * Event handler for when paging changes. Triggers change detection and loads definitions.
   */
  public onPagingChange() {
    this.ref.detectChanges();
    this.ref.markForCheck();
    this.loadDefinitions();
  }

  /**
   * Event handler for when filter changes. Loads definitions.
   */
  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => this.loadDefinitions(), 1000);
  }

  /**
   * Returns definitions which are allowed by currently set filter.
   */
  public getDefinitions(): ClientCustomFieldDefinition[] {
    return this.definitions;
  }

  /**
   * Returns type label for display.
   *
   * @param type
   */
  public getTypeLabel(type: string): string {
    const typeLabels: any = {
      text: this._(KEYS.CRUD.TYPE_TEXT),
      number: this._(KEYS.CRUD.TYPE_NUMBER),
      date: this._(KEYS.CRUD.TYPE_DATE),
      select: this._(KEYS.CRUD.TYPE_SELECT),
      boolean: this._(KEYS.CRUD.TYPE_BOOLEAN)
    };
    return typeLabels[type] || type;
  }

  /**
   * Loads definitions from API.
   */
  private loadDefinitions() {
    this.showPreloader();

    const filter = this.getQueryFilter();
    const opts = this.getQueryOpts();

    this.customFieldService.loadCustomFields(filter, opts)
      .then((result) => {
        this.definitions = result.rows;
        this.total = result.total;
        this.hidePreloader();
      })
      .catch(error => {
        this.hidePreloader();
        alert(this._(KEYS.GRID.ERROR_LOADING_DEFINITIONS));
        console.error(error);
      });
  }

  /**
   * Returns query filter by filter object.
   */
  private getQueryFilter() {
    const filter: any = {};
    Object.keys(this.filter).forEach((key) => {
      if (this.filter[key]) {
        filter[key] = {'$regex': this.filter[key], '$options': 'i'};
      }
    });
    return filter;
  }

  /**
   * Returns query opts.
   */
  private getQueryOpts() {
    return {limit: this.displayPerPage, offset: (this.gridPageNumber - 1) * this.displayPerPage};
  }

  /**
   * Shows preloader after 200ms.
   */
  private showPreloader() {
    this.showPreloaderTimeout = setTimeout(() => this.layoutService.showPreloader(), 200);
  }

  /**
   * Hides preloader.
   */
  private hidePreloader() {
    clearTimeout(this.showPreloaderTimeout);
    this.layoutService.hidePreloader();
  }
}
